/**
 * $cs_ corestream underscore helper ES6 module
 * ©2019 CoreStream Ltd
 * Developed by Michael Livesey, Ben Gallienne & Code {web development}
 *
 * helper unit, can be used in any Vue component on this context.
 * use: this.$cs_.methodname
 *
 * helper unit can also be used in any standalone ES6 module
 * use: import $cs_ from '@/js/plugins/cs-underscore-helpers';
 *
 * full description of available public methods is in PUBLIC section
 *
 * methods:
 *
 * wait(time, [resolveFlag])
 * loadJSONLocalStorage(key)
 * saveJSONSessionStorage(data, key)
 * isMatch(data1, data2)
 * deepAssign(target, ...source)
 * deepClone(source, [options])
 * get(obj, ...path)
 * classList.add(el, class) //class methods are for SVG and will only function with one class at a time in the parameter
 * classList.remove(el, class)
 * classList.contains(el, class)
 * timerStart(timerName)
 * timerAdd(timerName)
 * timerStop(timerName)
 * timerReset(timerName)
 * supportsListenerOptionObject()
 *
 * $cs_.querySelectorAllVue
 * $cs_.querySelectorVue
 * $cs_.closestVue
 * $cs_.closestAllVue
 * $cs_.emitTo
 * $cs_.emitToAll
 *
 * @format
 */

/* eslint no-console: 0, eqeqeq: 0 */

import appSettings from '@/js/appSettings';

const $cs_ = {};
const $cs_Private = {};

//PRIVATE PROPERTIES
let $_logs = {};
let $_timers = {};
let $_isTelemetry = false;
let $_supportsListenerOptionObject = undefined; //Boolean
let $_appNode = undefined;

export default $cs_;

//PRIVATE METHODS

$cs_Private.isMatchingObjects = function (source, target, sourceArray = [source], targetArray = [target], isMatch = true) {
	let i, j;
	let sourceObjectsArray = [];
	let targetObjectsArray = [];
	j = 0;
	while (j < sourceArray.length && isMatch === true) {
		let sKeys = Object.keys(sourceArray[j]);
		let tKeys = Object.keys(targetArray[j]);
		if (sKeys.length === tKeys.length) {
			i = 0;
			while (i < sKeys.length && isMatch === true) {
				if (tKeys.includes(sKeys[i])) {
					if (typeof sourceArray[j][sKeys[i]] === 'object' && sourceArray[j][sKeys[i]] != undefined) {
						if (typeof targetArray[j][sKeys[i]] === 'object' && targetArray[j][sKeys[i]] != undefined) {
							sourceObjectsArray.push(sourceArray[j][sKeys[i]]);
							targetObjectsArray.push(targetArray[j][sKeys[i]]);
						} else {
							isMatch = false;
						}
					} else {
						if (sourceArray[j][sKeys[i]] !== targetArray[j][sKeys[i]]) isMatch = false;
					}
				} else {
					isMatch = false;
				}
				i++;
			}
		} else {
			isMatch = false;
		}
		j++;
	}
	if (isMatch === false) {
		return false;
	} else if (sourceObjectsArray.length === 0) {
		return true;
	} else {
		return $cs_Private.isMatchingObjects(source, target, sourceObjectsArray, targetObjectsArray, isMatch); //tail optimized recursion
	}
};

/**
 * @public
 * @description
 * Fast Deep Assign
 * method works in a similar way to Object.assign except assignment is deep with nested Objects
 * at present only one source object is permitted ... this will be improved soon!
 * @param {Object, Array} source
 * @param {Object, Array} target
 */
$cs_Private.deepAssign = (target, source, targetArray = [target], sourceArray = [source]) => {
	if (source != undefined && target != undefined) {
		let sourceObjectsArray = [];
		let targetObjectsArray = [];
		for (let j = 0; j < sourceArray.length; j++) {
			let keys = Object.keys(sourceArray[j]);

			for (let i = 0; i < keys.length; i++) {
				let isPrimitiveType = sourceArray[j][keys[i]] == undefined || (typeof sourceArray[j][keys[i]] !== 'object' && typeof sourceArray[j][keys[i]] !== 'function');

				if (isPrimitiveType === true) {
					targetArray[j][keys[i]] = sourceArray[j][keys[i]];
				} else {
					let propertyType = Object.prototype.toString.call(sourceArray[j][keys[i]]);

					if (propertyType === '[object Object]') {
						let targetPropertyType = Object.prototype.toString.call(targetArray[j][keys[i]]);

						if (targetPropertyType !== '[object Object]') targetArray[j][keys[i]] = {};
						sourceObjectsArray.push(sourceArray[j][keys[i]]);
						targetObjectsArray.push(targetArray[j][keys[i]]);
					} else if (propertyType === '[object Array]') {
						let targetPropertyType = Object.prototype.toString.call(targetArray[j][keys[i]]);

						if (targetPropertyType !== '[object Array]') targetArray[j][keys[i]] = [];
						sourceObjectsArray.push(sourceArray[j][keys[i]]);
						targetObjectsArray.push(targetArray[j][keys[i]]);
					}
				}
			}
		}
		if (sourceObjectsArray.length === 0) {
			return target;
		} else {
			return $cs_Private.deepAssign(target, source, targetObjectsArray, sourceObjectsArray); //tail optimized recursion
		}
	}
};

/**
 * @public
 * @description
 * Fast Deep Clone Object and Freeze target - upto 6 times faster than loDash method
 * Will clone Objects/Arrays/Sets/Maps and any of the aforementioned if they contain files/blobs
 * Will not clone a file or blob at root level
 * Functions are ignored and excluded from clone but will not throw an error if contained in an object
 * Circular objects will cause an infinity loop
 * TODO - add option to handle circular references (will be slower)
 * @param {Object | Array | Map | Set} source
 * @param {Object} optionsObject
 * @returns {Object | Array | Map | Set}
 */
$cs_Private.deepCloneFreeze = (source, optionsObject = {freeze: false}, sourceArray = [source], target, targetArray) => {
	if (source != undefined) {
		let sourceObjectsArray = [];
		let targetObjectsArray = [];

		for (let j = 0, jLen = sourceArray.length; j < jLen; j++) {
			let keys = sourceArray[j];
			let sourceType = 'Object';

			if (Array.isArray(sourceArray[j]) === true) sourceType = 'Array';
			else if (sourceArray[j] instanceof Map) sourceType = 'Map';
			else if (sourceArray[j] instanceof Set) sourceType = 'Set';
			else keys = Object.keys(sourceArray[j]);

			if (target == undefined) {
				if (sourceType === 'Object') target = {};
				else if (sourceType === 'Array') target = [];
				else if (sourceType === 'Map') target = new Map();
				else if (sourceType === 'Set') target = new Set();
				targetArray = [target];
			}

			for (let key of keys) {
				let value;

				if (sourceType === 'Object') value = sourceArray[j][key];
				else if (sourceType === 'Array' || sourceType === 'Set') value = key;
				else if (sourceType === 'Map') [key, value] = key;

				let isPrimitiveType = value == undefined || (typeof value !== 'object' && typeof value !== 'function');
				let newValue;

				if (isPrimitiveType === true) {
					newValue = value;
				} else {
					let propertyType = Object.prototype.toString.call(value);

					switch (propertyType) {
						case '[object Object]':
							newValue = {};
							sourceObjectsArray.push(value);
							targetObjectsArray.push(newValue);
							break;
						case '[object Array]':
							newValue = [];
							sourceObjectsArray.push(value);
							targetObjectsArray.push(newValue);
							break;
						case '[object Map]':
							newValue = new Map();
							sourceObjectsArray.push(value);
							targetObjectsArray.push(newValue);
							break;
						case '[object Set]':
							newValue = new Set();
							sourceObjectsArray.push(value);
							targetObjectsArray.push(newValue);
							break;
						case '[object File]':
							newValue = new File([value], value.name, {
								type: value.type
							});
							break;
						case '[object Blob]':
							newValue = new Blob([value], {
								type: value.type
							});
							break;
						default:
							newValue = undefined;
					}
				}
				if (sourceType === 'Object') targetArray[j][key] = newValue;
				else if (sourceType === 'Array') targetArray[j].push(newValue);
				else if (sourceType === 'Map') targetArray[j].set(key, newValue);
				else if (sourceType === 'Set') targetArray[j].add(newValue);
			}
			if (optionsObject.freeze === true) {
				if (sourceType === 'Object' || sourceType === 'Array') Object.freeze(targetArray[j]);
			}
		}
		if (sourceObjectsArray.length === 0) return target;
		else return $cs_Private.deepCloneFreeze(source, optionsObject, sourceObjectsArray, target, targetObjectsArray); //tail optimized recursion
	}
};

/**
 * @public
 * @description
 * Fast Deep Clone Object with extra options of filter and freeze
 * filter is provided as a function of same type as array method where v,[i],[a] parameters are provided with a Boolean test for inclusion
 * e.g. {filter: (v,i,a) => v != undefined}
 * TODO need source keys as well as target keys
 * @param {Object, Array} source
 * @param {Object} optionsObject
 * @returns {Object}
 */
$cs_Private.deepCloneFilter = (source, optionsObject = {filter: () => false, freeze: false}, target = Array.isArray(source) ? [] : {}, sourceArray = [source], targetArray = [target]) => {
	if (source != undefined) {
		let sourceObjectsArray = [];
		let targetObjectsArray = [];
		for (let j = 0; j < sourceArray.length; j++) {
			let keys = Object.keys(sourceArray[j]);

			for (let i = 0; i < keys.length; i++) {
				if (optionsObject.filter != undefined && optionsObject.filter(sourceArray[j][keys[i]], keys[i], sourceArray[j]) === false) {
					//do nothing
				} else if (typeof sourceArray[j][keys[i]] === 'object' && sourceArray[j][keys[i]] == undefined) {
					//typeof null === 'object' bug exclusion
					targetArray[j][keys[i]] = sourceArray[j][keys[i]];
				} else if (Array.isArray(sourceArray[j][keys[i]])) {
					if (Array.isArray(targetArray[j])) {
						targetArray[j].push([]);
						targetObjectsArray.push(targetArray[j][targetArray[j].length - 1]);
					} else {
						targetArray[j][keys[i]] = [];
						targetObjectsArray.push(targetArray[j][keys[i]]);
					}
					sourceObjectsArray.push(sourceArray[j][keys[i]]);
				} else if (typeof sourceArray[j][keys[i]] === 'object') {
					//Regular Expression exclusion
					if (sourceArray[j][keys[i]].constructor !== RegExp) {
						if (Array.isArray(targetArray[j])) {
							targetArray[j].push({});
							targetObjectsArray.push(targetArray[j][targetArray[j].length - 1]);
						} else {
							targetArray[j][keys[i]] = {};
							targetObjectsArray.push(targetArray[j][keys[i]]);
						}
						sourceObjectsArray.push(sourceArray[j][keys[i]]);
					}
					//function exclusion
				} else if (typeof sourceArray[j][keys[i]] !== 'function') {
					if (Array.isArray(targetArray[j])) targetArray[j].push(sourceArray[j][keys[i]]);
					else targetArray[j][keys[i]] = sourceArray[j][keys[i]];
				}
			}
			if (optionsObject.freeze != undefined && optionsObject.freeze === true) Object.freeze(targetArray[j]);
		}
		if (sourceObjectsArray.length === 0) {
			return target;
		} else {
			return $cs_Private.deepCloneFilter(source, optionsObject, target, sourceObjectsArray, targetObjectsArray); //tail optimized recursion
		}
	}
};

/**
 * @public
 * @description
 * private method to determine match or not
 * @param {Object} node
 * @param {Object} query
 * @returns {Boolean}
 */
$cs_Private.queryVueNodeMatch = (node, query) => {
	let result = false;
	if (
		(query.name == undefined || node.$options.name === query.name) &&
		(query.ref == undefined || node.$refs[query.ref] != undefined) &&
		(query.listener == undefined || node.$listeners[query.listener] != undefined) &&
		(query.method == undefined || typeof node[query.method] === 'function')
	) {
		result = true;
		if (query.dataKey != undefined) {
			//if the query is a path
			if (Array.isArray(query.dataKey)) {
				let dataKeyValue = $cs_.get(node.$data, ...query.dataKey);
				result = dataKeyValue != undefined ? true : false;
				if (query.dataValue != undefined) result = dataKeyValue === query.dataValue ? true : false;
			} else {
				result = node.$data[query.dataKey] != undefined ? true : false;
				if (query.dataValue != undefined) result = node.$data[query.dataKey] === query.dataValue ? true : false;
			}
		}
		if (query.propsKey != undefined && result !== false) {
			//if the query is a path
			if (Array.isArray(query.propsKey)) {
				let propsKeyValue = $cs_.get(node.$props, ...query.propsKey);
				result = propsKeyValue != undefined ? true : false;
				if (query.propsValue != undefined) result = propsKeyValue === query.propsValue ? true : false;
			} else {
				result = node.$props[query.propsKey] != undefined ? true : false;
				if (query.propsValue != undefined) result = node.$props[query.propsKey] === query.propsValue ? true : false;
			}
		}
		if (query.thisKey != undefined && result !== false) {
			//if the query is a path
			if (Array.isArray(query.thisKey)) {
				let propsThisValue = $cs_.get(node, ...query.thisKey);
				result = propsThisValue != undefined ? true : false;
				if (query.thisValue != undefined) result = propsThisValue === query.thisValue ? true : false;
			} else {
				result = node[query.thisKey] != undefined ? true : false;
				if (query.thisValue != undefined) result = node[query.thisKey] === query.thisValue ? true : false;
			}
		}
		return result;
	} else {
		return false;
	}
};

//PUBLIC METHODS

/**
 * @public
 * @description
 * timeout promise - time in milliseconds
 * resolveFlag is optional and defaults to true - sets the timeout Promise return to either resolve or reject
 * use reject to trigger an error in an async try catch block, for example when using Promise.race() use $cs_.wait to create a rejection timeout for another promise (e.g. fetch)
 * $cs_.wait can also be used to asyc css animations and transitions
 * @param {Number} time
 * @param {Boolean} [resolveFlag]
 */
$cs_.wait = (time, resolveFlag = true) => {
	if (resolveFlag === true) {
		return new Promise((resolve) => setTimeout(() => resolve(), time));
	} else {
		return new Promise((resolve, reject) => setTimeout(() => reject(), time));
	}
};

/**
 * @public
 * @description
 * Checkes if passed item is a true object. Works with all types and it's resistant to falsy results
 * for items such as Array, Date, Set, Null or Undefined
 * @param { Any } item
 * @returns { Boolean }
 */
$cs_.isObject = (item) => {
	return Object.prototype.toString.call(item) === '[object Object]';
};

/**
 * @public
 * @description
 * Checks if passed item is Empty according to lo-dash illogical rules
 * null and undefined - true
 * Boolean/Number - true
 * Empty String - true
 * String - false
 * Empty Object or Array - true
 * Object or Array - false
 * @param { Any } item
 * @returns { Boolean }
 */
$cs_.isEmpty = (value) => {
	return value == undefined || Object.keys(value).length === 0;
};

/**
 * @public
 * @description
 * finds a match in an object or array for backwards compatibility with illogical lodash _.find method
 * @param { Any } item
 * @returns { Boolean }
 */
$cs_.find = (obj, fn) => {
	if (Array.isArray(obj)) {
		return obj.find(fn);
	} else {
		let keys = Object.keys(obj);
		for (let key of keys) {
			if (fn(obj[key])) return obj[key];
		}
	}
};

//WEB STORAGE

/**
 * @public
 * @description
 * sets the localStorage to an Object path
 * @param {String} key
 */
$cs_.loadJSONLocalStorage = (key) => {
	if (localStorage[key] != undefined) {
		return JSON.parse(localStorage[key]);
	} else return undefined;
};

/**
 * @public
 * @description
 * sets the localStorage to an Object path
 * @param {*} data
 * @param {String} key
 */
$cs_.saveJSONLocalStorage = (data, key) => {
	localStorage[key] = JSON.stringify(data);
};

/**
 * @public
 * @description
 * sets the sessionStorage to an Object path
 * @param {String} key
 */
$cs_.loadJSONSessionStorage = (key) => {
	if (sessionStorage[key] != undefined) {
		return JSON.parse(sessionStorage[key]);
	} else return undefined;
};

/**
 * @public
 * @description
 * sets the sessionStorage to an Object path
 * @param {*} data
 * @param {String} key
 */
$cs_.saveJSONSessionStorage = (data, key) => {
	sessionStorage[key] = JSON.stringify(data);
};

//OBJECT CLONE/ASSIGN METHODS

/**
 * @public
 * @description
 * compares two things of any type and determine if the are a match
 * matches objects and arrays for an exact deep match - object key order in not important
 * @param {Mixed} data1
 * @param {Mixed} data2
 * @returns {Boolean}
 */
$cs_.isMatch = function (data1, data2) {
	if (data1 === data2) return true;
	else if (data1 == undefined || data2 == undefined) return false;
	else if (typeof data1 !== 'object' || typeof data2 !== 'object') return false;
	let result = $cs_Private.isMatchingObjects(data1, data2);
	return result;
};

/**
 * @public
 * @description
 * Fast Deep Assign
 * method works in a similar way to Object.assign except assignment is deep with nested Objects
 * any number of source Objects can be provided as parameters listed one after the other - ..Rest
 * @param {Object, Array} target
 * @param {Object, Array} source
 * @returns {Object}
 */
$cs_.deepAssign = (target, ...source) => {
	let len = source.length;
	for (let i = 0; i < len; i++) {
		target = $cs_Private.deepAssign(target, source[i]);
	}
	return target;
};

/**
 * @public
 * @description
 * Fast Deep Clone Object with extra optional object for filter and Object.freeze options
 * filter is provided as a function of same type as array method where v,[i],[a] parameters are provided with a Boolean test for inclusion
 * e.g. {filter: (v,i,a) => v != undefined}
 * freeze is boolean
 * e.g. {freeze: true}
 * @param {Object, Array} source
 * @param {Object} [optionsObject] optional
 * @returns {Object}
 */
$cs_.deepClone = (source, optionsObject) => {
	if (source == undefined || typeof source !== 'object') return source;
	let objectType = Object.prototype.toString.call(source);

	if (objectType === '[object File]') return new File([source], source.name, {type: source.type});
	else if (objectType === '[object File]') return new Blob([source], {type: value.type});
	else if (optionsObject?.filter == undefined) return $cs_Private.deepCloneFreeze(source, optionsObject);
	else return $cs_Private.deepCloneFilter(source, optionsObject);
};

/**
 * @public
 * @description
 * Asynchronous deep cloning using the in-built structuredClone algorithm, used by web worked
 * returns the clone as a Promise only
 * NOTE: if object contains methods - e.g. a class - this method will throw an error
 * above deepClone with ignore methods and return a stripped Object/Array
 * Only call this method asynchronously in a try catch block!
 * @param {Mixed}
 * @returns {Promise}
 */
$cs_.structuredClone = (obj) => {
	return new Promise((resolve, reject) => {
		const {port1, port2} = new MessageChannel();
		port2.onmessage = (e) => resolve(e.data);
		port1.postMessage(obj);
	});
};

//OBJECT GET

/**
 * @public
 * @description
 * Core stream GET - returns undefined if path is undefined and/or property is undefined
 * never throws an error
 *
 * If you want to get the value for theObject.some.path.to.get
 * you would do: $cs_.get(theObject, some, path, to, get)
 *
 * *Note:* this will fail if you are only trying to get one path so ... don't
 *
 * @param {Object} obj - the object to get from
 * @param {...string} path - ...rest converts remaining parameters into an Array
 */
$cs_.get = (obj, ...path) => {
	let i = 0;
	let len = path.length - 1;
	while (obj != undefined && i < len) {
		obj = obj[path[i]];
		i++;
	}
	return obj && i === len ? obj[path[i]] : undefined;
};

//OBJECT SET

/**
 *
 * @param {Mixed} value
 * @param {Object} obj
 * @param  {...String} path
 */
$cs_.set = (value, obj, ...path) => {
	let i = 0;
	let len = path.length - 1;
	while (obj != undefined && i < len) {
		if (obj[path[i]] === undefined) obj[path[i]] = {};
		obj = obj[path[i]];
		i++;
	}
	if (obj && i === len) {
		obj[path[i]] = value;
	}
};

//classList IE11 polyfill due to unsupported SVG classList methods - className will also not work for SVG on any browser
$cs_.classList = {};

/**
 * @public
 * @description
 * classList add - this will add one class or multiple classes separated by a space
 * @param {Object} el
 * @param {String} addClass
 */
$cs_.classList.add = function (el, addClass) {
	let classList;
	if (el != undefined) {
		classList = el.getAttribute('class');
		if (classList == undefined) {
			addClass.trim();
			el.setAttribute('class', addClass);
		} else if (!classList.includes(addClass)) {
			addClass.trim();
			if (classList.length !== 0) classList += ' ' + addClass;
			else classList = addClass;
			el.setAttribute('class', classList);
		}
	}
};

/**
 * @public
 * @description
 * classList contains - this will test if a classList contain one class - does not work on multiple classes yet
 * @param {Object} el
 * @param {String} containsClass
 */
$cs_.classList.contains = function (el, containsClass) {
	let classList;
	if (el != undefined) {
		classList = el.getAttribute('class');
		if (classList == undefined || !classList.includes(containsClass)) {
			return false;
		}
		return true;
	} else {
		return false;
	}
};

/**
 * @public
 * @description
 * classList remove - this will remove one class from a classList - does not work on multiple classes yet
 * @param {Object} el
 * @param {String} removeClass
 */
$cs_.classList.remove = function (el, removeClass) {
	let classList, index;
	if (el != undefined) {
		classList = el.getAttribute('class');
		if (classList == undefined) {
			index = -1;
		} else {
			index = classList.indexOf(removeClass);
		}
		if (index !== -1) {
			if (index === 0) classList = classList.replace(removeClass, '');
			else classList = classList.replace(' ' + removeClass, '');
			el.setAttribute('class', classList);
		}
	}
};

// REGISTERING METHODS - TELEMETRY

/**
 * Register a component appearance in the app
 * @param { String } componentName
 */
$cs_.logRegisterComponent = (componentName) => {
	if ($_logs[componentName] == undefined) $_logs[componentName] = +1;
	else $_logs[componentName] = $_logs[componentName] += 1;
};

/**
 *
 * @param { String } componentName
 * Clears component registry log or all logs if component name not present
 */
$cs_.logResetComponent = (componentName) => {
	if (componentName != undefined && $_logs[componentName] != undefined) delete $_logs[componentName];
	else $_logs = {};
};

//TIMING METHODS - TELEMETRY

/**
 *
 * @param {String} timerName
 * @param {Number} time
 */
$cs_.timerStart = (timerName) => {
	//console.log($_isTelemetry);
	if ($_isTelemetry === true) {
		if ($_timers[timerName] == undefined) {
			$_timers[timerName] = {timer: 0, count: 0, t1: performance.now()};
		} else {
			$_timers[timerName].t1 = performance.now();
		}
	}
};

/**
 * @public
 * @description
 * adds a current split time from last timerStart to the named timer - as in split timing
 * @param {String} timerName
 */
$cs_.timerAdd = (timerName) => {
	if ($_isTelemetry === true) {
		if ($_timers[timerName] != undefined && $_timers[timerName].t1) {
			$_timers[timerName].timer += performance.now() - $_timers[timerName].t1;
			$_timers[timerName].count++;
			$_timers[timerName].t1 = 0;
		}
	}
};

/**
 * @public
 * @description
 * wipes the timer and sets the time from start to stop
 * @param {String} timerName
 */
$cs_.timerStop = (timerName) => {
	if ($_isTelemetry === true) {
		if ($_timers[timerName] != undefined && $_timers[timerName].t1) {
			$_timers[timerName].timer = performance.now() - $_timers[timerName].t1;
			$_timers[timerName].count++;
			$_timers[timerName].t1 = 0;
		}
	}
};

/**
 * @public
 * @description
 * resets all the timers if timerName != undefined
 * @param {String} [timerName] optional
 */
$cs_.timerReset = (timerName) => {
	if ($_isTelemetry === true) {
		if (timerName != undefined) {
			if ($_timers[timerName] != undefined) {
				delete $_timers[timerName];
			}
		} else {
			$_timers = {};
		}
	}
};

/**
 * @public
 * @description
 * sets the supports listener options Boolean state and returns state
 */
$cs_.supportsListenerOptionObject = () => {
	if ($_supportsListenerOptionObject == undefined) {
		$_supportsListenerOptionObject = false;
		try {
			let options = Object.defineProperty({}, 'passive', {
				get: function () {
					$_supportsListenerOptionObject = true;
					return false;
				}
			});
			window.addEventListener('testPassive', null, options);
			window.removeEventListener('testPassive', null, options);
		} catch (error) {
			$_supportsListenerOptionObject = false;
		}
	}
	return $_supportsListenerOptionObject;
};

/**
 * @public
 * @description
 * Returns the logs object
 * @returns { Object }
 */
$cs_.getLogs = () => $_logs;

/**
 * @public
 * @description
 * returns the timers Objects
 */
$cs_.getTimers = () => $_timers;

//BASE64ENCODING

/**
 * @public
 * @description
 * @param {String} str
 * First we escape the string using encodeURIComponent to get the UTF-8 encoding of the characters,
 * then we convert the percent encodings into raw bytes, and finally feed it to btoa() function.
 */
$cs_.$base64EncodeUnicode = function (str) {
	if (typeof str === 'number') str = str.toString(10);
	const utf8Bytes = encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function (match, p1) {
		return String.fromCharCode('0x' + p1);
	});

	return btoa(utf8Bytes);
};

//JAVASCRITP MAPS

/**
 * @description
 * finds and item in a map using the callback(value, key) => {} similar to Map.forEach
 * returns the matching value OR undefined
 * @param {Map} map
 * @param {Function} callback
 * @returns {Mixed}
 */
$cs_.findInMap = function (map, callback) {
	for (let mapEntry of map) {
		if (callback(mapEntry[1], mapEntry[0]) === true) return mapEntry[1];
	}
};

/**
 * @description
 * filters a map using the callback(value, key) => {} similar to Map.forEach
 * returns an array of values that pass
 * @param {Map} map
 * @param {Function} callback
 * @returns {Array}
 */
$cs_.filterInMap = function (map, callback) {
	let results = [];
	for (let mapEntry of map) {
		if (callback(mapEntry[1], mapEntry[0]) === true) results.push(mapEntry[1]);
	}
	return results;
};

//VUE VIRTUAL DOM QUERY METHODS

/**
 * @public
 * @description
 * queries vue nodes and returns result depending on query string
 * query is an optional object in the form - use as many or few keys as desired: {name:String, dataKey:String, dataValue:Mixed, method:String, ref:String}
 * example usage - this.$cs_.querySelectorAllVue(this.$root, {method:'loadCascade'});
 * ternary in nodeArray param is to allow this.$root to scan root node as well $children as if a call to document.querySelector
 * @param {Object} node - pass in current this or this.$root (which scans the whole document)
 * @param {Object} [query] optional
 * @returns {Array}
 */
$cs_.querySelectorAllVue = (node, query = {}, nodeArray = node.$parent != undefined ? node.$children : [node], result = []) => {
	let nestedArray = [];

	for (let i = 0, len = nodeArray.length; i < len; i++) {
		if ($cs_Private.queryVueNodeMatch(nodeArray[i], query)) result.push(nodeArray[i]);
		if (nodeArray[i].$children.length > 0) nestedArray.push(...nodeArray[i].$children);
	}
	if (nestedArray.length === 0) return result;
	else return $cs_.querySelectorAllVue(node, query, nestedArray, result);
};

/**
 * @public
 * @description
 * queries vue nodes and returns result depending on query string
 * query is an object in the format - {name:String, dataKey:String, dataValue:Mixed, method:String, ref:String} use as few or many keys as desired
 * example usage - this.$cs_.querySelectorVue(this.$root, {method:'loadCascade'});
 * terary in nodeArray param is to allow this.$root to scan root node as well $children as if a call to document.querySelector
 * @param {Object} node - pass in current this or this.$root (which scans the whole document)
 * @param {Object} query
 * @returns {Object}
 */
$cs_.querySelectorVue = (node, query = {}, nodeArray = node.$parent != undefined ? node.$children : [node]) => {
	let nestedArray = [];

	for (let i = 0, len = nodeArray.length; i < len; i++) {
		if ($cs_Private.queryVueNodeMatch(nodeArray[i], query)) return nodeArray[i];
		if (nodeArray[i].$children.length > 0) nestedArray.push(...nodeArray[i].$children);
	}
	if (nestedArray.length === 0) return null;
	else return $cs_.querySelectorVue(node, query, nestedArray);
};

/**
 * @public
 * @description
 * queries vue nodes and returns result depending on query string
 * works similar to Browser API closest method - however query is now Vue specific. Query can use as many or few keys as desired: {name:String, dataKey:String, dataValue:Mixed, method:String, ref:String}
 * example usage - this.$cs_.closestVue(this, {method:'loadCascade'});
 * @param {Object} node - pass in current this, this.$parent, or any queried node
 * @param {Object} query
 * @returns {Object}
 */
$cs_.closestVue = (node, query = {}) => {
	let result = null;

	while (node.$parent && result == undefined) {
		if ($cs_Private.queryVueNodeMatch(node.$parent, query)) result = node.$parent;
		else node = node.$parent;
	}
	return result;
};

/**
 * @public
 * @description
 * queries vue nodes and returns result depending on query string
 * returns all matching parents - query is Vue specific. Query can use as many or few keys as desired: {name:String, dataKey:String, dataValue:Mixed, method:String, ref:String}
 * example usage - this.$cs_.closestAllVue(this, {method:'loadCascade'});
 * @param {Object} node - pass in current this, this.$parent, or any queried node
 * @param {Object} query
 * @returns {Array}
 */
$cs_.closestAllVue = (node, query = {}, result = []) => {
	while (node.$parent) {
		if ($cs_Private.queryVueNodeMatch(node.$parent, query)) result.push(node.$parent);
		else node = node.$parent;
	}
	return result;
};

/**
 * @public
 * @description
 * emits to first matching parent only - this allow user to avoid repeated $emit through multiple levels
 * specify the listener query as a string as you would $emit
 * example usage - this.$cs_.emitTo(this, 'listener-name', payload);
 * @param {Object} node - pass in current this, this.$parent, or any queried node
 * @param {String} listener
 * @param {Mixed} ...payload
 * @returns {Void}
 */
$cs_.emitTo = (node, listener, ...payload) => {
	while (node.$parent) {
		if (node.$parent.$listeners[listener] != undefined) {
			node.$parent.$listeners[listener](...payload);
			return;
		} else {
			node = node.$parent;
		}
	}
};

/**
 * @public
 * @description
 * emits to all parents matching the query
 * specify the listener query as a string as you would $emit
 * example usage - this.$cs_.emitToAll(this, 'listener-name', payload);
 * @param {Object} node - pass in current this, this.$parent, or any queried node
 * @param {String} listener
 * @param {Mixed} ...payload
 * @returns {Void}
 */
$cs_.emitToAll = (node, listener, ...payload) => {
	while (node.$parent) {
		if (node.$parent.$listeners[listener] != undefined) {
			node.$parent.$listeners[listener](...payload);
		} else {
			node = node.$parent;
		}
	}
};

/**
 * @public
 * @description
 * sets the Vue app level node internal to ES6 module for use across all ES6 modules
 * this is useful for accessing Vue system level methods and querySelector from the root node
 * use this same method to set to undefined on beforeDestroy
 * @param
 */
$cs_.setAppNode = (node) => {
	$_appNode = node;
};

/**
 * @public
 * @description
 * sets the app level node internal to ES6 module for use across all ES6 modules
 * @param
 */
$cs_.getAppNode = () => {
	return $_appNode;
};

/**
 * @public
 * @description
 * gets all the tab-able nodes filters and sorts them
 * use attribute binding index:componentname e.g. :tabindex-override="`${index}:cs-grid-kpicard`"
 */
$cs_.getTabNodeList = () => {
	let time = performance.now();
	let tabableQuery = 'a:not([disabled],.cs-skip-link--link), button:not([disabled]), input[type=text]:not([disabled]), [tabindex]:not([disabled], .cs-skip-link--link)';
	let tabables = document.querySelectorAll(tabableQuery);
	let tabNodeList = Array.prototype.filter.call(tabables, (item) => item.offsetWidth > 0 && item.tabIndex >= '0');
	tabNodeList = tabNodeList.sort((a, b) => {
		let tabindexOverrideA = a.getAttribute('tabindex-override');
		if (tabindexOverrideA == undefined) return 0;
		let tabindexOverrideB = b.getAttribute('tabindex-override');
		if (tabindexOverrideB == undefined) return 0;
		if (tabindexOverrideA.substr(tabindexOverrideA.indexOf(':')) !== tabindexOverrideB.substr(tabindexOverrideB.indexOf(':'))) return 0;
		return parseInt(tabindexOverrideA) - parseInt(tabindexOverrideB);
	});
	//console.log(performance.now() - time);
	return tabNodeList;
};

/**
 * @public
 * @description
 * Checks if the passed object is a valid JSON string and retruns parsed object or false
 * @param { Object } obj
 * @returns { Object | Boolean }
 */
$cs_.tryParseObject = (obj) => {
	try {
		const parsedObj = JSON.parse(obj);
		if (parsedObj && typeof parsedObj == 'object') return parsedObj;
	} catch (error) {}

	return false;
};

/**
 * @public
 * @description
 * Returns unified string value
 * If any other type of value, returns value
 *
 * @param { Any } value
 * @returns { String | Any }
 */
$cs_.getValueLowerCase = function (value) {
	if (typeof value == 'string') return value.toLowerCase();
	else return value;
};

/**
 * @public
 * @description
 * Checks a given object for the existence of a give key, ignoring case
 *
 * @param {Object} jsonObject The object to iterate over
 * @param {String} keyName The key name to search for
 * @returns {Mixed} This will return undefined if not found or the jsonObject[key] value if found - which could be null
 */
$cs_.getValueByObjectKeyIgnoringCase = function (jsonObject, keyName) {
	let keyNameLowerCase = keyName.toLowerCase();
	let PascalKeyName = keyName.substring(0, 1).toUpperCase() + keyName.slice(1);

	if (jsonObject[keyName] !== undefined) return jsonObject[keyName];
	else if (jsonObject[keyNameLowerCase] !== undefined) return jsonObject[keyNameLowerCase];
	else if (jsonObject[PascalKeyName] !== undefined) return jsonObject[PascalKeyName];

	let keys = Object.keys(jsonObject);
	let keyIndex = keys.findIndex((v) => v.toLowerCase() === keyNameLowerCase);

	if (keyIndex !== -1) return jsonObject[keys[keyIndex]];
};

/**
 * @public
 * @description
 * Removes null, empty and undefined elements from an object
 *
 * @returns {object}
 */
$cs_.recursivelyRemoveNullAndEmptyAndUndefinedFromObject = function (object, o = JSON.parse(JSON.stringify(object))) {
	let keys = Object.keys(o);
	for (let key of keys) {
		if (o[key] == undefined) delete o[key];
		else if (typeof o[key] === 'object') {
			$cs_.recursivelyRemoveNullAndEmptyAndUndefinedFromObject(null, o[key]); // Recurse.
			if (Object.keys(o[key]).length === 0) delete o[key];
		}
	}
	return o;
};

/**
 * @public
 * @description
 * Returns boolean whenever passed node is a child of the passed parent node.
 * @param { Object } node
 * @param { Object } parent
 * @returns { Boolean }
 */
$cs_.childOf = function (node, parent) {
	let child = node;
	while (child != null && parent != undefined) {
		if (child === parent) return true;
		child = child.parentNode;
	}
	return false;
};

/**
 * @public
 * @description
 * Removes all html tags from a string
 *
 * @param { String } input The input string
 *
 * @returns { String }
 */
$cs_.removeHtmlTags = function(input) {
	if (typeof input != 'string') return input;
	input = input.replace(/&nbsp;/g, () => String.fromCharCode(160));
	return input.replace(/(<([^>]+)>)/gi, '');
};



//END OF PUBLIC METHODS

//IMMEDIATELY INVOKED CODE

(async function isTelemetry() {
	let settings = await appSettings.get();
	$_isTelemetry = Boolean(settings.VUE_APP_TELEMETRY_ON) ? true : false;
	if ($_isTelemetry === true) {
		window.$csOutputTimers = $cs_.getTimers();
		window.$csLogs = $cs_.getLogs();
	}
})();

//MEMORY LEAK
let _memoryCheckCount = 0;
class MemoryCheck {
	constructor(id, element) {
		console.log(`MEMORY ALLOCATED: ${id}`);
		this.ref = new WeakRef(element);
		this.id = id;
		this.start();
		_memoryCheckCount++;
	}

	start() {
		if (this.timer) return;

		this.count = 0;

		const tick = () => {
			// Get the element from the weak reference, if it still exists
			const element = this.ref.deref();
			if (element !== undefined) {
				this.count++;
			} else {
				_memoryCheckCount--;
				// The element doesn't exist anymore
				console.log('GARBAGE COLLECTION', this.id, `MEMORYCHECK HEAP: ${_memoryCheckCount}`);
				if (_memoryCheckCount <= 0){
					_memoryCheckCount = 0;
					console.log('ALL MEMORYCHECKS COLLECTED!');
				}
				this.stop();
				this.ref = null;
			}
		};

		tick();
		this.timer = setInterval(tick, 1000);
	}

	stop() {
		if (this.timer) {
			clearInterval(this.timer);
			this.timer = 0;
		}
	}
}

$cs_.setMemoryCheck = (id, obj) => {
	return new MemoryCheck(id, obj);
};

/**
 * @public
 * @description
 * Returns a string with a capitalised first letter
 * @param { String } word
 * @returns { String }
 */
$cs_.ucFirst = (word) => {
	if (word.length === 0) return "";
	const firstLetter = word.charAt(0).toUpperCase();
	const remainingLetters = word.slice(1);
	return `${firstLetter}${remainingLetters}`;
}
